import { useEffect, useState } from 'react';

export type CrawlProgressStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'unknown';

export interface CrawlProgress {
  status: CrawlProgressStatus;
  pagesDiscovered: number;
  pagesCrawled: number;
  pagesErrored: number;
  emittedAt: string | null;
  isStreaming: boolean;
}

const TERMINAL: ReadonlySet<CrawlProgressStatus> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

const INITIAL: CrawlProgress = {
  status: 'unknown',
  pagesDiscovered: 0,
  pagesCrawled: 0,
  pagesErrored: 0,
  emittedAt: null,
  isStreaming: false,
};

// Subscribes to /api/crawls/:crawlId/progress (SSE). The Worker pushes
// one event every ~2s and closes the stream on terminal status, so the
// hook simply mirrors what the server sends. Same-origin EventSource
// forwards the Cloudflare Access cookie automatically.
export function useCrawlProgress(crawlId: string | undefined): CrawlProgress {
  const [state, setState] = useState<CrawlProgress>(INITIAL);

  useEffect(() => {
    if (!crawlId) {
      setState(INITIAL);
      return;
    }

    let closed = false;
    const source = new EventSource(`/api/crawls/${crawlId}/progress`);
    setState((s) => ({ ...s, isStreaming: true }));

    source.onmessage = (ev) => {
      if (closed) return;
      try {
        const payload = JSON.parse(ev.data) as Partial<CrawlProgress> & {
          event?: string;
        };
        if (payload.event === 'timeout' || payload.event === 'error') {
          source.close();
          setState((s) => ({ ...s, isStreaming: false }));
          return;
        }
        setState((s) => ({
          status: (payload.status as CrawlProgressStatus) ?? s.status,
          pagesDiscovered: payload.pagesDiscovered ?? s.pagesDiscovered,
          pagesCrawled: payload.pagesCrawled ?? s.pagesCrawled,
          pagesErrored: payload.pagesErrored ?? s.pagesErrored,
          emittedAt: payload.emittedAt ?? s.emittedAt,
          isStreaming: !TERMINAL.has(
            (payload.status as CrawlProgressStatus) ?? s.status,
          ),
        }));
        if (payload.status && TERMINAL.has(payload.status as CrawlProgressStatus)) {
          source.close();
        }
      } catch {
        // Malformed event — drop it. The next tick will overwrite state.
      }
    };

    source.onerror = () => {
      // Browser auto-reconnects EventSource on transient failure; we
      // only flip isStreaming off if the stream is actually closed.
      if (source.readyState === EventSource.CLOSED) {
        setState((s) => ({ ...s, isStreaming: false }));
      }
    };

    return () => {
      closed = true;
      source.close();
    };
  }, [crawlId]);

  return state;
}
